import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Grid,
  List,
  Download,
  Eye,
  MoreVertical,
  FileText,
  Calendar,
  User,
  Edit,
  Trash2,
  Share,
  Star,
  BarChart3,
  Upload
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import ActionMenu from '../../components/ui/ActionMenu';
import NewDocumentForm from '../../components/forms/NewDocumentForm';
import DocumentEditModal from '../../components/ui/DocumentEditModal';
import CountUp from '../../components/CountUp';
import DocumentSubtypeButtons from '../../components/ui/DocumentSubtypeButtons';
import { SearchFilters } from '../../types';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import { apiService } from '../../services/api';
import { settingsService } from '../../services/api/settings';
import '../../styles/background-effects.css';

// Fonction utilitaire pour obtenir le résumé d'un document
const getDocumentSummary = (doc: any): string => {
  return doc.description || doc.summary || 'Aucune description disponible.';
};

// Fonction utilitaire pour extraire la première phrase de la page spécifique contenant le terme recherché
const getSearchContext = (content: string, searchTerm: string, firstOccurrencePage: number, totalPages: number, summary: string = '', description: string = ''): string => {
  if (!searchTerm) {
    return "Information trouvée dans le document";
  }
  
  // Normaliser le terme de recherche (minuscules, supprimer espaces multiples)
  const normalizedSearchTerm = searchTerm.toLowerCase().trim().replace(/\s+/g, ' ');
  const searchTerms = normalizedSearchTerm.split(' ').filter(term => term.length > 0);
  // Normaliser la recherche exacte (avec espaces) - déclaré tôt pour être accessible partout
  const exactSearchPhrase = normalizedSearchTerm;
  
  // Utiliser uniquement le contenu (pas description ni summary qui sont des métadonnées)
  const text = content || '';
  if (!text || text.length === 0) {
    return "Information trouvée dans le document";
  }
  
  // Si first_occurrence_page n'est pas valide, retourner un message par défaut
  if (!firstOccurrencePage || firstOccurrencePage < 1 || !totalPages || totalPages < 1) {
    return "Information trouvée dans le document";
  }
  
  // Calculer approximativement où se trouve la page de première occurrence dans le contenu
  const words = text.split(/\s+/).filter(w => w.length > 0);
  const totalWords = words.length;
  const wordsPerPage = Math.ceil(totalWords / totalPages);
  
  // Calculer les indices de début et fin de la page
  const pageStartIndex = Math.max(0, (firstOccurrencePage - 1) * wordsPerPage);
  const pageEndIndex = Math.min(totalWords, firstOccurrencePage * wordsPerPage);
  
  // Extraire le texte de la page spécifique
  const pageWords = words.slice(pageStartIndex, pageEndIndex);
  const pageText = pageWords.join(' ');
  
  // Diviser le texte de la page en phrases
  const pageSentences = pageText
    .split(/(?<=[.!?])\s+|(?<=[.!?])(?=[A-Z])|(?<=[.!?])\n/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
  
  // Mots-clés pour identifier les sections de page de garde/sommaire
  const skipKeywords = [
    'table des matières', 'sommaire', 'table of contents', 'index',
    'résumé', 'abstract', 'introduction', 'avant-propos', 'préface',
    'page de garde', 'couverture', 'titre', 'auteur', 'date'
  ];
  
  // Patterns typiques du sommaire (numérotation, titres courts, etc.)
  const isTableOfContentsPattern = (sentence: string): boolean => {
    const lower = sentence.toLowerCase();
    
    // Vérifier les mots-clés de sommaire
    if (skipKeywords.some(keyword => lower.includes(keyword))) {
      return true;
    }
    
    // Patterns de numérotation de sommaire (ex: "1.1", "I.", "A)", etc.)
    const tocNumberPattern = /^[\dIVX]+[\.\)]\s*[A-Z]|^[A-Z][\.\)]\s*[A-Z]|^\d+\.\d+/;
    if (tocNumberPattern.test(sentence.trim())) {
      return true;
    }
    
    // Phrases très courtes suivies de numéros de page (ex: "Introduction ................ 5")
    const pageNumberPattern = /\.{3,}\s*\d+$|\.{2,}\s*\d+$/;
    if (sentence.length < 80 && pageNumberPattern.test(sentence)) {
      return true;
    }
    
    // Phrases qui sont juste des titres (pas de ponctuation finale, très courtes)
    if (sentence.length < 50 && !/[.!?]$/.test(sentence)) {
      return true;
    }
    
    return false;
  };
  
  // Fonction pour détecter les articles français avant un mot
  const detectArticleBefore = (sentence: string, wordIndex: number): string => {
    if (wordIndex <= 0) return '';
    
    // Articles français courants
    const articles = [
      'le ', 'la ', 'les ', 'l\'', 'l’',
      'un ', 'une ', 'des ',
      'du ', 'de la ', 'de l\'', 'de l\'',
      'au ', 'aux ', 'à la ', 'à l\'', 'à l\'',
      'ce ', 'cet ', 'cette ', 'ces ',
      'mon ', 'ma ', 'mes ',
      'ton ', 'ta ', 'tes ',
      'son ', 'sa ', 'ses ',
      'notre ', 'nos ',
      'votre ', 'vos ',
      'leur ', 'leurs '
    ];
    
    // Prendre les 10 caractères avant le mot (suffisant pour les articles les plus longs)
    const beforeText = sentence.substring(Math.max(0, wordIndex - 10), wordIndex).toLowerCase();
    
    // Chercher l'article le plus long qui correspond (pour gérer "de la" avant "du")
    let foundArticle = '';
    let maxLength = 0;
    
    for (const article of articles) {
      if (beforeText.endsWith(article) && article.length > maxLength) {
        foundArticle = article;
        maxLength = article.length;
      }
    }
    
    // Si un article est trouvé, retourner l'article avec la casse originale
    if (foundArticle) {
      const articleStartIndex = wordIndex - foundArticle.length;
      return sentence.substring(articleStartIndex, wordIndex);
    }
    
    return '';
  };
  
  // Fonction pour extraire la partie de la phrase qui commence par le premier terme recherché (ou son article)
  const extractContextFromSentence = (sentence: string, terms: string[]): string => {
    const lowerSentence = sentence.toLowerCase();
    const firstTerm = terms[0].toLowerCase();
    
    // Trouver la position du premier terme dans la phrase
    const firstTermIndex = lowerSentence.indexOf(firstTerm);
    
    if (firstTermIndex === -1) {
      return sentence.trim();
    }
    
    // Extraire directement à partir du premier terme recherché
    // Chercher le début du mot (pas au milieu d'un autre mot)
    let actualStartIndex = firstTermIndex;
    
    // Vérifier que c'est bien le début d'un mot (pas au milieu)
    if (actualStartIndex > 0) {
      const charBefore = sentence[actualStartIndex - 1];
      // Si le caractère avant est une lettre, ce n'est pas le début d'un mot
      if (/[a-zA-ZÀ-ÿ]/.test(charBefore)) {
        // Chercher la prochaine occurrence
        const nextIndex = lowerSentence.indexOf(firstTerm, actualStartIndex + 1);
        if (nextIndex !== -1) {
          actualStartIndex = nextIndex;
        }
      }
    }
    
    // Détecter s'il y a un article avant le terme recherché
    const article = detectArticleBefore(sentence, actualStartIndex);
    
    // Ajuster l'index de début pour inclure l'article si trouvé
    if (article) {
      actualStartIndex = actualStartIndex - article.length;
    }
    
    // Extraire à partir du terme recherché (ou de l'article) jusqu'à la fin de la phrase
    let extracted = sentence.substring(actualStartIndex).trim();
    
    // Nombre minimum de caractères souhaité (environ 2 lignes comme la case 2)
    const minChars = 120;
    
    // Toujours chercher à atteindre le minimum, même si on doit continuer au-delà de la ponctuation
    // On prend d'abord tout le texte disponible depuis le début
    const fullTextFromStart = sentence.substring(actualStartIndex).trim();
    
    if (fullTextFromStart.length < minChars) {
      // Si le texte complet est plus court que le minimum, prendre tout
      extracted = fullTextFromStart;
    } else {
      // Si le texte est plus long, chercher la ponctuation qui nous donne au moins minChars
      // On parcourt toutes les ponctuations et on prend celle qui nous donne >= minChars
      let bestPunctuationIndex = -1;
      
      // Chercher toutes les ponctuations dans le texte, en continuant jusqu'à trouver une qui donne >= minChars
      let searchIndex = actualStartIndex;
      while (searchIndex < sentence.length) {
        const nextPeriod = sentence.indexOf('.', searchIndex);
        const nextExclamation = sentence.indexOf('!', searchIndex);
        const nextQuestion = sentence.indexOf('?', searchIndex);
        
        const nextPunctuation = Math.min(
          nextPeriod !== -1 ? nextPeriod : Infinity,
          nextExclamation !== -1 ? nextExclamation : Infinity,
          nextQuestion !== -1 ? nextQuestion : Infinity
        );
        
        if (nextPunctuation !== Infinity && nextPunctuation < sentence.length) {
          // Calculer la longueur jusqu'à cette ponctuation (inclure la ponctuation)
          const textUntilPunctuation = sentence.substring(actualStartIndex, nextPunctuation + 1).trim();
          
          // Si cette longueur atteint ou dépasse le minimum, c'est notre candidat
          if (textUntilPunctuation.length >= minChars) {
            bestPunctuationIndex = nextPunctuation;
            break; // On a trouvé une ponctuation qui nous donne assez de texte
          }
          
          // Sinon, continuer à chercher après cette ponctuation (on IGNORE cette ponctuation et on continue)
          searchIndex = nextPunctuation + 1;
        } else {
          // Plus de ponctuation, prendre au moins minChars caractères
          break;
        }
      }
      
      // Si on a trouvé une ponctuation qui nous donne assez de texte, l'utiliser
      if (bestPunctuationIndex !== -1) {
        extracted = sentence.substring(actualStartIndex, bestPunctuationIndex + 1).trim();
      } else {
        // Sinon, prendre au moins minChars caractères (même sans ponctuation)
        extracted = fullTextFromStart.substring(0, minChars).trim();
      }
    }
    
    // Vérifier si le texte original continue après l'extrait
    const fullText = sentence.substring(actualStartIndex).trim();
    const isTruncated = extracted.length < fullText.length;
    
    // Si tronqué, ajouter les trois points de suspension
    if (isTruncated) {
      extracted = extracted + '...';
    }
    
    return extracted;
  };
  
  // Filtrer les phrases de la page pour exclure celles qui ressemblent à du sommaire
  const sentences = pageSentences.filter(s => {
    // Filtrer les phrases trop courtes (probablement des titres)
    if (s.length < 30) return false;
    
    // Ignorer les phrases qui sont encore des éléments de sommaire
    if (isTableOfContentsPattern(s)) return false;
    
    // Ignorer les phrases qui ne contiennent que des titres/chapitres
    if (!/[.!?]$/.test(s) && s.length < 100) return false;
    
    return s.length > 0;
  });
  
  // Si aucune phrase valide n'est trouvée dans la page, chercher dans une plage élargie
  if (sentences.length === 0) {
    // Élargir la recherche à la page précédente et suivante
    const extendedStartIndex = Math.max(0, pageStartIndex - wordsPerPage);
    const extendedEndIndex = Math.min(totalWords, pageEndIndex + wordsPerPage);
    const extendedWords = words.slice(extendedStartIndex, extendedEndIndex);
    const extendedText = extendedWords.join(' ');
    
    const extendedSentences = extendedText
      .split(/(?<=[.!?])\s+|(?<=[.!?])(?=[A-Z])|(?<=[.!?])\n/)
      .map(s => s.trim())
      .filter(s => {
        if (s.length < 30) return false;
        if (isTableOfContentsPattern(s)) return false;
        if (!/[.!?]$/.test(s) && s.length < 100) return false;
        return s.length > 0;
      });
    
    // Chercher d'abord la séquence exacte dans les phrases élargies
    for (const sentence of extendedSentences) {
      const normalizedSentence = sentence.toLowerCase();
      
      if (normalizedSentence.includes(exactSearchPhrase)) {
        const exactIndex = sentence.toLowerCase().indexOf(exactSearchPhrase);
        
        // Détecter s'il y a un article avant la séquence exacte
        const article = detectArticleBefore(sentence, exactIndex);
        
        // Ajuster l'index de début pour inclure l'article si trouvé
        let startIndex = exactIndex;
        if (article) {
          startIndex = exactIndex - article.length;
        }
        
        const fullTextFromStart = sentence.substring(startIndex).trim();
        const minChars = 120;
        let extracted = fullTextFromStart;
        
        if (fullTextFromStart.length < minChars) {
          // Si le texte complet est plus court que le minimum, prendre tout
          extracted = fullTextFromStart;
        } else {
          // Si le texte est plus long, chercher la ponctuation qui nous donne au moins minChars
          let bestPunctuationIndex = -1;
          
          // Chercher toutes les ponctuations dans le texte
          let searchIndex = startIndex;
          while (searchIndex < sentence.length) {
            const nextPeriod = sentence.indexOf('.', searchIndex);
            const nextExclamation = sentence.indexOf('!', searchIndex);
            const nextQuestion = sentence.indexOf('?', searchIndex);
            
            const nextPunctuation = Math.min(
              nextPeriod !== -1 ? nextPeriod : Infinity,
              nextExclamation !== -1 ? nextExclamation : Infinity,
              nextQuestion !== -1 ? nextQuestion : Infinity
            );
            
            if (nextPunctuation !== Infinity && nextPunctuation < sentence.length) {
              const textUntilPunctuation = sentence.substring(startIndex, nextPunctuation + 1).trim();
              
              if (textUntilPunctuation.length >= minChars) {
                bestPunctuationIndex = nextPunctuation;
                break;
              }
              
              searchIndex = nextPunctuation + 1;
            } else {
              break;
            }
          }
          
          if (bestPunctuationIndex !== -1) {
            extracted = sentence.substring(startIndex, bestPunctuationIndex + 1).trim();
          } else {
            extracted = fullTextFromStart.substring(0, minChars).trim();
          }
        }
        
        // Vérifier si le texte original continue après l'extrait
        const isTruncated = extracted.length < fullTextFromStart.length;
        
        // Si tronqué, ajouter les trois points de suspension
        if (isTruncated) {
          extracted = extracted + '...';
        }
        
        return extracted.trim();
      }
    }
    
    // Chercher les mots dans l'ordre dans les phrases élargies
    for (const sentence of extendedSentences) {
      const normalizedSentence = sentence.toLowerCase();
      const containsAllTerms = searchTerms.every(term => 
        normalizedSentence.includes(term.toLowerCase())
      );
      
      if (containsAllTerms) {
        let lastIndex = -1;
        let allInOrder = true;
        
        for (const term of searchTerms) {
          const termIndex = normalizedSentence.indexOf(term.toLowerCase(), lastIndex + 1);
          if (termIndex === -1) {
            allInOrder = false;
            break;
          }
          lastIndex = termIndex;
        }
        
        if (allInOrder) {
          const firstTermIndex = normalizedSentence.indexOf(searchTerms[0].toLowerCase());
          const lastTermIndex = normalizedSentence.lastIndexOf(searchTerms[searchTerms.length - 1].toLowerCase());
          const distance = lastTermIndex - firstTermIndex;
          
          if (distance < 100) {
            const extracted = extractContextFromSentence(sentence, searchTerms);
            return extracted;
          }
        }
      }
    }
    
    // Chercher tous les mots ensemble dans les phrases élargies
    for (const sentence of extendedSentences) {
      const normalizedSentence = sentence.toLowerCase();
      const containsAllTerms = searchTerms.every(term => 
        normalizedSentence.includes(term.toLowerCase())
      );
      if (containsAllTerms) {
        const extracted = extractContextFromSentence(sentence, searchTerms);
        return extracted;
      }
    }
    
    // En dernier recours, chercher au moins le premier terme
    for (const sentence of extendedSentences) {
      const normalizedSentence = sentence.toLowerCase();
      if (normalizedSentence.includes(searchTerms[0].toLowerCase())) {
        const extracted = extractContextFromSentence(sentence, [searchTerms[0]]);
        return extracted;
      }
    }
  }
  
  // Chercher d'abord la séquence exacte de mots (dans l'ordre)
  for (const sentence of sentences) {
    const normalizedSentence = sentence.toLowerCase();
    
    // Vérifier si la phrase contient la séquence exacte recherchée
    if (normalizedSentence.includes(exactSearchPhrase)) {
      // Trouver la position de la séquence exacte
      const exactIndex = normalizedSentence.indexOf(exactSearchPhrase);
      const actualIndex = sentence.toLowerCase().indexOf(exactSearchPhrase);
      
      // Détecter s'il y a un article avant la séquence exacte
      const article = detectArticleBefore(sentence, actualIndex);
      
      // Ajuster l'index de début pour inclure l'article si trouvé
      let startIndex = actualIndex;
      if (article) {
        startIndex = actualIndex - article.length;
      }
      
      // Extraire à partir de la séquence exacte (ou de l'article)
      const fullTextFromStart = sentence.substring(startIndex).trim();
      const minChars = 120;
      let extracted = fullTextFromStart;
      
      if (fullTextFromStart.length < minChars) {
        // Si le texte complet est plus court que le minimum, prendre tout
        extracted = fullTextFromStart;
      } else {
        // Si le texte est plus long, chercher la ponctuation qui nous donne au moins minChars
        let bestPunctuationIndex = -1;
        
        // Chercher toutes les ponctuations dans le texte
        let searchIndex = startIndex;
        while (searchIndex < sentence.length) {
          const nextPeriod = sentence.indexOf('.', searchIndex);
          const nextExclamation = sentence.indexOf('!', searchIndex);
          const nextQuestion = sentence.indexOf('?', searchIndex);
          
          const nextPunctuation = Math.min(
            nextPeriod !== -1 ? nextPeriod : Infinity,
            nextExclamation !== -1 ? nextExclamation : Infinity,
            nextQuestion !== -1 ? nextQuestion : Infinity
          );
          
          if (nextPunctuation !== Infinity && nextPunctuation < sentence.length) {
            const textUntilPunctuation = sentence.substring(startIndex, nextPunctuation + 1).trim();
            
            if (textUntilPunctuation.length >= minChars) {
              bestPunctuationIndex = nextPunctuation;
              break;
            }
            
            searchIndex = nextPunctuation + 1;
          } else {
            break;
          }
        }
        
        if (bestPunctuationIndex !== -1) {
          extracted = sentence.substring(startIndex, bestPunctuationIndex + 1).trim();
        } else {
          extracted = fullTextFromStart.substring(0, minChars).trim();
        }
      }
      
      // Vérifier si le texte original continue après l'extrait
      const isTruncated = extracted.length < fullTextFromStart.length;
      
      // Si tronqué, ajouter les trois points de suspension
      if (isTruncated) {
        extracted = extracted + '...';
      }
      
      return extracted.trim();
    }
  }
  
  // Si la séquence exacte n'est pas trouvée, chercher les mots dans l'ordre (proximité)
  for (const sentence of sentences) {
    const normalizedSentence = sentence.toLowerCase();
    
    // Vérifier si tous les termes sont présents
    const containsAllTerms = searchTerms.every(term => 
      normalizedSentence.includes(term.toLowerCase())
    );
    
    if (containsAllTerms) {
      // Vérifier que les termes sont dans l'ordre (proximité)
      let lastIndex = -1;
      let allInOrder = true;
      
      for (const term of searchTerms) {
        const termIndex = normalizedSentence.indexOf(term.toLowerCase(), lastIndex + 1);
        if (termIndex === -1) {
          allInOrder = false;
          break;
        }
        lastIndex = termIndex;
      }
      
      // Si les termes sont dans l'ordre et proches (moins de 50 caractères entre le premier et le dernier)
      if (allInOrder) {
        const firstTermIndex = normalizedSentence.indexOf(searchTerms[0].toLowerCase());
        const lastTermIndex = normalizedSentence.lastIndexOf(searchTerms[searchTerms.length - 1].toLowerCase());
        const distance = lastTermIndex - firstTermIndex;
        
        // Si les termes sont proches (dans un rayon de 100 caractères), c'est probablement la bonne phrase
        if (distance < 100) {
          const extracted = extractContextFromSentence(sentence, searchTerms);
          return extracted;
        }
      }
    }
  }
  
  // Si les mots ne sont pas dans l'ordre, chercher quand même tous les mots dans la même phrase
  for (const sentence of sentences) {
    const normalizedSentence = sentence.toLowerCase();
    
    // Vérifier si la phrase contient tous les termes recherchés
    const containsAllTerms = searchTerms.every(term => 
      normalizedSentence.includes(term.toLowerCase())
    );
    
    if (containsAllTerms) {
      // Extraire la partie qui commence par le premier terme recherché
      const extracted = extractContextFromSentence(sentence, searchTerms);
      return extracted;
    }
  }
  
  // En dernier recours, chercher au moins un terme (mais privilégier le premier)
  for (const sentence of sentences) {
    const normalizedSentence = sentence.toLowerCase();
    
    // Vérifier si la phrase contient au moins le premier terme (le plus important)
    if (normalizedSentence.includes(searchTerms[0].toLowerCase())) {
      const extracted = extractContextFromSentence(sentence, [searchTerms[0]]);
      return extracted;
    }
  }
  
  // Si rien n'est trouvé dans la page, retourner un message par défaut
  return "Information trouvée dans le document";
};

// Fonction utilitaire pour obtenir les tags d'un document
const getDocumentTags = (doc: any): string[] => {
  if (!doc.tags) return [];
  if (Array.isArray(doc.tags)) {
    return doc.tags.map((tag: any) => typeof tag === 'string' ? tag : tag.name || tag);
  }
  if (typeof doc.tags === 'string') {
    try {
      const parsed = JSON.parse(doc.tags);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return doc.tags.split(',').map((t: string) => t.trim());
    }
  }
  return [];
};


const DocumentsPage: React.FC = () => {
  const { toasts, showSuccess, showError, removeToast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'search' | 'documents'>('overview');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any>(null);
  const [showDocumentMenu, setShowDocumentMenu] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [viewingDocument, setViewingDocument] = useState<string | null>(null);

  // États pour les sous-types de documents
  const [selectedSubtypes, setSelectedSubtypes] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [summary, setSummary] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [documentSubtypes, setDocumentSubtypes] = useState<any[]>([]);
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    type: [],
    project: '',
    team: '',
    uploadedBy: '',
    tags: []
  });

  // Extraire les noms des sous-types pour les boutons de recherche
  const availableSubtypes = useMemo(() => {
    return documentSubtypes.map((subtype: any) => subtype.name);
  }, [documentSubtypes]);

  // Réinitialiser l'état de visualisation quand on revient à la page
  useEffect(() => {
    setViewingDocument(null);
  }, []);

  // Charger les documents, utilisateurs, projets et équipes depuis l'API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('📄 Chargement des documents et données associées...');
        
        const [
          documentsData,
          usersData,
          projectsData,
          teamsData,
          typesData,
          subtypesData
        ] = await Promise.all([
          apiService.getDocuments(),
          settingsService.getAllUsers(),
          apiService.getProjects(),
          apiService.getTeams(),
          settingsService.getDocumentTypes(),
          settingsService.getDocumentSubtypes()
        ]);
        
        console.log('✅ Documents chargés:', documentsData.length);
        console.log('✅ Utilisateurs chargés:', usersData.length);
        console.log('✅ Projets chargés:', projectsData.length);
        console.log('✅ Équipes chargées:', teamsData.length);
        console.log('✅ Types de documents chargés:', typesData.length);
        console.log('✅ Sous-types chargés:', subtypesData.length);
        
        setDocuments(documentsData);
        setUsers(usersData);
        setProjects(projectsData);
        setTeams(teamsData);
        setDocumentTypes(typesData);
        setDocumentSubtypes(subtypesData);
      } catch (err) {
        console.error('❌ Erreur lors du chargement:', err);
        showError('Erreur de chargement', 'Impossible de charger les données');
        setDocuments([]);
        setUsers([]);
        setProjects([]);
        setTeams([]);
        setDocumentTypes([]);
        setDocumentSubtypes([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError]);

  // Filtrage des documents
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      // Recherche par nom et description
      const matchesQuery = !searchQuery || 
        (doc.title || doc.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.description && doc.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (doc.tags && Array.isArray(doc.tags) && doc.tags.some((tag: any) => {
          const tagName = typeof tag === 'string' ? tag : tag.name || '';
          return tagName.toLowerCase().includes(searchQuery.toLowerCase());
        }));

      // Filtres avancés
      const matchesType = !filters.type || filters.type.length === 0 || filters.type.includes(doc.file_type || doc.type);
      const matchesProject = !filters.project || doc.project === filters.project;
      const matchesTeam = !filters.team || doc.team === filters.team;
      const matchesUploader = !filters.uploadedBy || doc.uploaded_by?.toString() === filters.uploadedBy;

      return matchesQuery && matchesType && matchesProject && matchesTeam && matchesUploader;
    });
  }, [searchQuery, filters, documents]);

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📈';
      case 'image': return '🖼️';
      default: return '📁';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCloudIcon = (provider?: string) => {
    switch (provider) {
      case 'google-drive': return '🔵';
      case 'onedrive': return '🔷';
      case 'dropbox': return '🔵';
      case 'sharepoint': return '🟦';
      default: return '💾';
    }
  };

  const getUserName = (userId?: string | number | null) => {
    if (!userId) return 'Utilisateur inconnu';
    const user = users.find((u: any) => u.id.toString() === userId.toString());
    return user ? (user.full_name || user.name || `${user.first_name} ${user.last_name}`) : 'Utilisateur inconnu';
  };

  const getProjectName = (projectId?: string | number) => {
    if (!projectId) return null;
    const project = projects.find((p: any) => p.id.toString() === projectId.toString());
    return project ? project.name : null;
  };

  const getTeamName = (teamId?: string | number) => {
    if (!teamId) return null;
    const team = teams.find((t: any) => t.id.toString() === teamId.toString());
    return team ? team.name : null;
  };

  // Actions sur les documents
  const handleNewDocument = async (data: any) => {
    setUploading(true);
    try {
      const newDocument = await apiService.uploadDocument(data);
      setDocuments(prev => [newDocument, ...prev]);
      setShowNewDocumentModal(false);
      showSuccess('Document uploadé avec succès', `${data.name} a été ajouté à votre bibliothèque`);
      
      // Refresh automatique après 3 secondes pour voir le résumé généré
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      
    } catch (err) {
      showError('Erreur lors de l\'upload', 'Impossible d\'uploader le document. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId: string) => {
    const doc = documents.find(d => String(d.id) === String(docId));
    if (!doc) {
      showError('Erreur', 'Document non trouvé');
      return;
    }

    try {
      showSuccess('Téléchargement en cours...', 'Préparation du fichier');
      
      // Essayer d'utiliser l'URL du fichier si disponible
      const fileUrl = doc.file_url || doc.cloud_url || doc.cloudUrl;
      
      if (fileUrl && fileUrl !== '#') {
        // Si c'est une URL complète, télécharger directement
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = doc.title || doc.name || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess('Téléchargement réussi', 'Le fichier a été téléchargé');
      } else {
        // Sinon, essayer de récupérer le fichier via l'API
        // Pour l'instant, afficher un message d'information
        showError('Téléchargement indisponible', 'L\'URL du fichier n\'est pas disponible. Veuillez contacter l\'administrateur.');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      showError('Erreur de téléchargement', 'Impossible de télécharger le fichier');
    }
  };

  const handleView = async (docId: string | number) => {
    console.log('🔍 Ouverture du document:', docId, 'Type:', typeof docId);
    
    // Conversion en string et validation de l'ID du document
    const docIdStr = String(docId);
    if (!docId || docIdStr.trim() === '') {
      console.error('❌ ID du document invalide:', docId);
      showError('Erreur de navigation', 'ID du document invalide');
      return;
    }
    
    try {
      // Indiquer que le document est en cours d'ouverture
      setViewingDocument(docIdStr);
      
      // Ouvrir le document dans un nouvel onglet pour que l'utilisateur puisse continuer à voir ses résultats
      const url = `/workspace/documents/${docIdStr}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      
      console.log('✅ Document ouvert dans un nouvel onglet');
      
      // Réinitialiser l'état après un court délai
      setTimeout(() => {
        setViewingDocument(null);
      }, 1000);
    } catch (error) {
      console.error('❌ Erreur lors de l\'ouverture du document:', error);
      showError('Erreur de navigation', 'Impossible d\'ouvrir le document');
      setViewingDocument(null);
    }
  };

  // Fonction pour gérer la sélection des sous-types
  const handleSubtypeSelect = (subtype: string) => {
    setSelectedSubtypes(prev => {
      if (prev.includes(subtype)) {
        return prev.filter(s => s !== subtype);
      } else {
        return [...prev, subtype];
      }
    });
  };

  const handleShare = async (docId: string) => {
    const doc = documents.find(d => String(d.id) === String(docId));
    if (!doc) {
      showError('Erreur', 'Document non trouvé');
      return;
    }

    try {
      // Construire l'URL de partage vers le visualiseur de document
      const shareUrl = `${window.location.origin}/workspace/documents/${docId}`;
      
      // Copier le lien dans le presse-papiers
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        showSuccess('Lien copié', 'Le lien de partage a été copié dans le presse-papiers');
      } else {
        // Fallback pour les navigateurs qui ne supportent pas clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          showSuccess('Lien copié', 'Le lien de partage a été copié dans le presse-papiers');
        } catch (err) {
          showError('Erreur', 'Impossible de copier le lien. Veuillez le copier manuellement: ' + shareUrl);
        }
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Erreur lors du partage:', error);
      showError('Erreur de partage', 'Impossible de copier le lien de partage');
    }
  };

  const handleEdit = (docId: string) => {
    const doc = documents.find(d => d.id === docId);
    if (doc) {
      setEditingDocument(doc);
      setShowEditDocumentModal(true);
    }
  };

  const handleUpdateDocument = async (data: any) => {
    if (!editingDocument) return;
    
    try {
      const updatedDocument = await apiService.updateDocument(editingDocument.id, data);
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === editingDocument.id 
            ? { ...doc, ...updatedDocument }
            : doc
        )
      );
      setShowEditDocumentModal(false);
      setEditingDocument(null);
      showSuccess('Document mis à jour', `${data.title || data.name} a été modifié avec succès`);
    } catch (err) {
      showError('Erreur lors de la modification', 'Impossible de modifier le document. Veuillez réessayer.');
    }
  };

  const handleDelete = async (docId: string) => {
    const doc = documents.find(d => d.id === docId);
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      try {
        await apiService.deleteDocument(docId);
        setDocuments(prev => prev.filter(d => d.id !== docId));
        showSuccess('Document supprimé', `${doc?.title || doc?.name} a été supprimé de votre bibliothèque`);
      } catch (err) {
        showError('Erreur lors de la suppression', 'Impossible de supprimer le document. Veuillez réessayer.');
      }
    }
  };

  const handleToggleFavorite = (docId: string) => {
    setDocuments(prev => 
      prev.map(doc => 
        doc.id === docId 
          ? { ...doc, isFavorite: !doc.isFavorite }
          : doc
      )
    );
  };

  // Fonctions de recherche IA
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const results = await apiService.searchDocuments(searchQuery);
      setSearchResults(results);
      setSummary(''); // Reset summary when new search
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      showError('Erreur de recherche', 'Impossible d\'effectuer la recherche. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSummarize = async () => {
    if (searchResults.length === 0) return;
    
    setLoading(true);
    try {
      const summaryText = await apiService.summarizeSearchResults(searchResults);
      setSummary(summaryText);
    } catch (err) {
      console.error('Erreur lors de la génération du résumé:', err);
      showError('Erreur de résumé', 'Impossible de générer le résumé. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Aurora Background Effect */}
      
      <div className="space-y-6 content-above-aurora">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Documents</h1>
            <p className="text-gray-600">
              {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} trouvé{filteredDocuments.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button 
              onClick={() => setShowNewDocumentModal(true)}
              icon={<FileText className="w-4 h-4" />}
            >
              Nouveau Document
            </Button>

          </div>
        </div>

        {/* Onglets */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Recherche IA
            </button>
            <button
              onClick={() => {
                setActiveTab('documents');
                setSearchQuery(''); // Vider le champ de recherche
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
          </nav>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Vue d'ensemble - Statistiques et aperçu */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500 rounded-bl-full opacity-10"></div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">Total Documents</p>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      <CountUp to={documents.length} duration={2} delay={0.2} className="text-2xl font-bold text-gray-900" />
                    </p>
                    <p className="text-sm font-medium text-blue-600">Tous les documents</p>
                  </div>
                  <div className="bg-blue-500 bg-opacity-20 p-3 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('documents')}
                  className="absolute top-4 right-4 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  Voir tous
                </Button>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-green-500 rounded-bl-full opacity-10"></div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">Ajoutés ce mois</p>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      <CountUp 
                        to={documents.filter(doc => {
                          const lastMonth = new Date();
                          lastMonth.setMonth(lastMonth.getMonth() - 1);
                          const uploadDate = new Date(doc.uploaded_at || doc.uploadedAt);
                          return uploadDate > lastMonth;
                        }).length} 
                        duration={1.5} 
                        delay={0.4} 
                        className="text-2xl font-bold text-gray-900" 
                      />
                    </p>
                    <p className="text-sm font-medium text-green-600">Ce mois-ci</p>
                  </div>
                  <div className="bg-green-500 bg-opacity-20 p-3 rounded-lg">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setActiveTab('documents');
                    setFilters(prev => ({ ...prev, query: 'nouveau' }));
                  }}
                  className="absolute top-4 right-4 text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  Filtrer
                </Button>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-purple-500 rounded-bl-full opacity-10"></div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">Collaborateurs</p>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      <CountUp 
                        to={new Set(documents.map(doc => doc.uploaded_by).filter(id => id)).size} 
                        duration={1.5} 
                        delay={0.6} 
                        className="text-2xl font-bold text-gray-900" 
                      />
                    </p>
                    <p className="text-sm font-medium text-purple-600">Équipe active</p>
                  </div>
                  <div className="bg-purple-500 bg-opacity-20 p-3 rounded-lg">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setActiveTab('documents');
                    setShowFilters(true);
                  }}
                  className="absolute top-4 right-4 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                >
                  Gérer
                </Button>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500 rounded-bl-full opacity-10"></div>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600 mb-1">Favoris</p>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      <CountUp 
                        to={documents.filter(doc => doc.isFavorite).length} 
                        duration={1.5} 
                        delay={0.8} 
                        className="text-2xl font-bold text-gray-900" 
                      />
                    </p>
                    <p className="text-sm font-medium text-orange-600">Documents marqués</p>
                  </div>
                  <div className="bg-orange-500 bg-opacity-20 p-3 rounded-lg">
                    <Star className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setActiveTab('documents');
                    setFilters(prev => ({ ...prev, query: 'favoris' }));
                  }}
                  className="absolute top-4 right-4 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                >
                  Voir
                </Button>
              </div>
            </div>

            {/* Documents récents */}
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents Récents</h3>
              <div className="space-y-3">
                {documents.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500 mb-3">Aucun document récent</p>
                    <p className="text-xs text-gray-400">Uploadez votre premier document pour commencer</p>
                  </div>
                ) : (
                  documents.slice(0, 5).map((doc) => (
                  <div key={doc.id} className="rounded-lg overflow-hidden hover:shadow-lg hover:shadow-gray-200/50 transition-all duration-300 hover:scale-[1.02] hover:bg-gray-100/80" style={{backgroundColor: 'rgb(225 228 228)'}}>
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getFileIcon(doc.file_type || doc.type)}</span>
                        <div>
                          <p className="font-medium text-gray-900">{doc.name}</p>
                          <p className="text-sm text-gray-500">
                            {doc.file_size_human || formatFileSize(doc.size)} • {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Date inconnue'}
                          </p>
                        </div>
                      </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleView(doc.id)}
                        disabled={viewingDocument === String(doc.id)}
                        className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                          viewingDocument === String(doc.id)
                            ? 'text-blue-500 bg-blue-50 cursor-wait'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-white'
                        }`}
                        title={viewingDocument === String(doc.id) ? "Ouverture en cours..." : "Voir le document"}
                      >
                        {viewingDocument === String(doc.id) ? (
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDownload(doc.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200 hover:scale-110"
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShare(doc.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200 hover:scale-110"
                        title="Partager"
                      >
                        <Share className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleFavorite(doc.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200 hover:scale-110"
                        title={doc.isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                      >
                        <Star className={`w-4 h-4 ${doc.isFavorite ? 'text-yellow-500 fill-current' : ''}`} />
                      </button>
                    </div>
                    </div>
                    
                    {/* Section Résumé du document */}
                    <div className="px-3 pb-3 border-t border-gray-200">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 mt-2 hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Résumé</h4>
                        <p className="text-sm text-gray-700 leading-relaxed mb-3">
                          {getDocumentSummary(doc)}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {getDocumentTags(doc).map((tag, index) => (
                            <span 
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:text-blue-900 transition-colors duration-150"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'search' && (
          <div className="space-y-6">
            {/* Sous-types de documents pour recherche IA */}
            <div className="bg-white rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Recherche par type de document</h3>
              <DocumentSubtypeButtons
                subtypes={availableSubtypes}
                onSubtypeSelect={(subtype) => {
                  setSearchQuery(subtype);
                  handleSearch();
                }}
                selectedSubtypes={[]}
                maxVisible={8}
              />
            </div>

            {/* Barre de recherche */}
            <Card>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Recherche Intelligente</h3>
                <p className="text-gray-600 mb-4">Effectuez une recherche sémantique dans vos documents</p>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Posez votre question ou tapez des mots-clés..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <Button onClick={handleSearch} disabled={loading}>
                    <Search className="w-4 h-4 mr-2" />
                    Rechercher
                  </Button>
                </div>
              </div>
            </Card>

            {/* Résultats de recherche */}
            {searchResults.length > 0 && (
              <Card>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Résultats de Recherche</h3>
                      <p className="text-gray-600">{searchResults.length} extraits trouvés</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {searchResults.map((result, index) => (
                      <div key={index} className="border rounded-lg p-6 space-y-4 hover:shadow-md transition-shadow">
                        {/* En-tête du document */}
                        <div className="flex items-center justify-between border-b pb-3">
                          <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                            <span>{getFileIcon(result.document_name || result.name || result.type)}</span>
                            <span>{result.document_name || result.name || result.title}</span>
                          </h4>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              Score: {typeof result.semantic_score === 'number' ? result.semantic_score.toFixed(0) : '85'}%
                            </span>
                          </div>
                        </div>

                        {/* Grille des informations */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Cellule 1: Nombre de pages */}
                          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-purple-600 font-medium">📊 Pages du document</span>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-purple-800">
                                {result.first_occurrence_page || 1}/{result.total_pages || 'N/A'}
                              </div>
                              <div className="text-sm text-purple-600">Page de première occurrence / Total</div>
                            </div>
                          </div>

                          {/* Cellule 2: Résumé du document */}
                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-blue-600 font-medium">📋 Résumé du document</span>
                            </div>
                            <p className="text-blue-700 text-sm leading-relaxed overflow-hidden" style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: '1.4',
                              maxHeight: '2.8em'
                            }}>
                              {result.summary || "Résumé non disponible"}
                            </p>
                          </div>

                          {/* Cellule 3: Information recherchée */}
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-green-600 font-medium">🔍 Information recherchée</span>
                            </div>
                            <p className="text-green-700 text-sm leading-relaxed">
         {getSearchContext(
           result.content || '', 
           searchQuery,
           result.first_occurrence_page || 1,
           result.total_pages || 1,
           result.summary || '',
           result.description || ''
         )}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-3 border-t">
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Ouvrir le fichier/document
                                if (result.document_id || result.id) {
                                  const documentId = result.document_id || result.id;
                                  
                                  // Créer un blob avec le contenu si disponible
                                  if (result.content) {
                                    const blob = new Blob([result.content], { type: 'text/plain' });
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.target = '_blank';
                                    link.rel = 'noopener noreferrer';
                                    link.click();
                                    URL.revokeObjectURL(url);
                                  } else {
                                    // Fallback: ouvrir dans un nouvel onglet avec l'ID du document
                                    window.open(`/workspace/documents/${documentId}`, '_blank', 'noopener,noreferrer');
                                  }
                                }
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir
                            </Button>
                            <ActionMenu
                              documentId={result.document_id || result.id}
                              documentName={result.document_name || result.name || result.title}
                              onView={() => {
                                const docId = result.document_id || result.id;
                                window.open(`/workspace/documents/${docId}`, '_blank', 'noopener,noreferrer');
                              }}
                              onShare={() => {
                                // Logique de partage
                                console.log('Partager le document:', result.document_name || result.name || result.title);
                              }}
                              onDownload={() => {
                                // Logique de téléchargement
                                console.log('Télécharger le document:', result.document_name || result.name || result.title);
                              }}
                              onEdit={undefined}
                              onCopy={() => {
                                // Logique de copie du lien
                                navigator.clipboard.writeText(window.location.href);
                                console.log('Lien copié');
                              }}
                              onFavorite={() => {
                                // Logique d'ajout aux favoris
                                console.log('Ajouter aux favoris:', result.document_name || result.name || result.title);
                              }}
                              onDelete={() => {
                                // Logique de suppression
                                console.log('Supprimer le document:', result.document_name || result.name || result.title);
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">Document ID: {result.document_id || result.id}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Résumé global */}
            {summary && (
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Résumé Intelligent</h3>
                  <p className="text-gray-600 mb-4">Synthèse générée par IA à partir des extraits trouvés</p>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{summary}</pre>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            {/* Barre de recherche et filtres */}
        <Card>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher par nom, contenu, tags..."
                value={searchQuery}
                onChange={setSearchQuery}
                icon={<Search className="w-5 h-5" />}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant={showFilters ? 'primary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
                icon={<Filter className="w-4 h-4" />}
              >
                Filtres
              </Button>
              <div className="flex border border-gray-300 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Filtres avancés */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200"
            >
              <div className="overflow-x-auto scrollbar-hide">
                <div className="flex gap-4 min-w-max pb-2">
                <div className="w-56">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de document</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.documentType || ''}
                    onChange={(e) => setFilters({...filters, documentType: e.target.value, documentSubtype: ''})}
                  >
                    <option value="">Tous les types</option>
                    {documentTypes.map((type: any) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div className="w-56">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sous-type</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.documentSubtype || ''}
                    onChange={(e) => setFilters({...filters, documentSubtype: e.target.value})}
                    disabled={!filters.documentType}
                  >
                    <option value="">Tous les sous-types</option>
                    {documentSubtypes
                      .filter((subtype: any) => {
                        const typeId = typeof subtype.document_type === 'object' 
                          ? subtype.document_type?.id 
                          : subtype.document_type;
                        return typeId && typeId.toString() === filters.documentType?.toString();
                      })
                      .map((subtype: any) => (
                        <option key={subtype.id} value={subtype.id}>{subtype.name}</option>
                      ))
                    }
                  </select>
                </div>
                <div className="w-56">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type de fichier</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.type?.join(',') || ''}
                    onChange={(e) => setFilters({...filters, type: e.target.value ? e.target.value.split(',') : []})}
                  >
                    <option value="">Tous les formats</option>
                    <option value="pdf">PDF</option>
                    <option value="doc,docx">Word</option>
                    <option value="xls,xlsx">Excel</option>
                    <option value="ppt,pptx">PowerPoint</option>
                    <option value="image">Images</option>
                  </select>
                </div>
                <div className="w-56">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Projet</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.project}
                    onChange={(e) => setFilters({...filters, project: e.target.value})}
                  >
                    <option value="">Tous les projets</option>
                    {projects.map((project: any) => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
                <div className="w-56">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Équipe</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.team}
                    onChange={(e) => setFilters({...filters, team: e.target.value})}
                  >
                    <option value="">Toutes les équipes</option>
                    {teams.map((team: any) => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
                <div className="w-56">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Uploadé par</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.uploadedBy}
                    onChange={(e) => setFilters({...filters, uploadedBy: e.target.value})}
                  >
                    <option value="">Tous les utilisateurs</option>
                    {users.map((user: any) => (
                      <option key={user.id} value={user.id}>{user.full_name || user.name || `${user.first_name} ${user.last_name}`}</option>
                    ))}
                  </select>
                </div>
                </div>
              </div>
            </motion.div>
          )}
        </Card>

        {/* Indicateur de chargement */}
        {loading && (
          <Card>
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-gray-600">Chargement des documents...</span>
            </div>
          </Card>
        )}

        {/* Liste des documents */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-20 h-20 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">Aucun document trouvé</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? `Aucun document ne correspond à "${searchQuery}"`
                : "Commencez par uploader votre premier document"}
            </p>
            <Button 
              variant="primary"
              icon={<Upload className="w-4 h-4" />}
              onClick={() => setShowNewDocumentModal(true)}
            >
              Uploader un document
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredDocuments.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="relative"
              >
                <Card className="p-4 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start justify-between mb-3">
                                          <span className="text-3xl">{getFileIcon(doc.file_type || doc.type || 'other')}</span>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm">{getCloudIcon(doc.cloudProvider)}</span>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowDocumentMenu(showDocumentMenu === doc.id ? null : doc.id)}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                                          <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">{doc.title || doc.name}</h3>
                  
                  <div className="space-y-2 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Date inconnue'}
                    </div>
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-1" />
                      {doc.uploaded_by_name || getUserName(doc.uploaded_by)}
                    </div>
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 mr-1" />
                      {formatFileSize(doc.size)}
                    </div>
                  </div>

                                      {(doc.tags && Array.isArray(doc.tags) && doc.tags.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(doc.tags && Array.isArray(doc.tags) ? doc.tags : []).slice(0, 2).map((tag: any) => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          {typeof tag === 'string' ? tag : tag.name || 'tag'}
                        </span>
                      ))}
                      {doc.tags.length > 2 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          +{doc.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex space-x-2">
                      <button 
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          viewingDocument === String(doc.id)
                            ? 'text-blue-500 bg-blue-50 cursor-wait'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                        onClick={() => handleView(doc.id)}
                        disabled={viewingDocument === String(doc.id)}
                        title={viewingDocument === String(doc.id) ? "Ouverture en cours..." : "Voir le document"}
                      >
                        {viewingDocument === String(doc.id) ? (
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button 
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        onClick={() => handleDownload(doc.id)}
                        title="Télécharger"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button 
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        onClick={() => handleToggleFavorite(doc.id)}
                        title={doc.isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                      >
                        <Star className={`w-4 h-4 ${doc.isFavorite ? 'text-yellow-500 fill-current' : ''}`} />
                      </button>
                    </div>
                    {doc.isPublic && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        Public
                      </span>
                    )}
                  </div>

                  {/* Menu contextuel */}
                  {showDocumentMenu === doc.id && (
                    <div className="absolute top-12 right-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[160px]">
                      <button
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        onClick={() => handleShare(doc.id)}
                      >
                        <Share className="w-4 h-4 mr-2" />
                        Partager
                      </button>
                      <button
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        onClick={() => handleEdit(doc.id)}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Éditer
                      </button>
                      <button
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                        onClick={() => handleDelete(doc.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </button>
                    </div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Document</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Taille</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Uploadé par</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Projet</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Cloud</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                                                      <span className="text-2xl">{getFileIcon(doc.file_type || doc.type || 'other')}</span>
                            <div>
                              <p className="font-medium text-gray-900">{doc.title || doc.name}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                                                {(doc.tags && Array.isArray(doc.tags) ? doc.tags : []).slice(0, 3).map((tag: any) => (
                                  <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                    {typeof tag === 'string' ? tag : tag.name || 'tag'}
                                  </span>
                                ))}
                            </div>
                          </div>
                        </div>
                      </td>
                                              <td className="py-3 px-4 text-sm text-gray-600">{formatFileSize(doc.file_size || doc.size || 0)}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{doc.uploaded_by_name || getUserName(doc.uploaded_by)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Date inconnue'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {getProjectName(doc.project) && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {getProjectName(doc.project)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <span className="text-lg">{getCloudIcon(doc.cloudProvider)}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <button 
                            className={`p-2 rounded-lg transition-all duration-200 ${
                              viewingDocument === String(doc.id)
                                ? 'text-blue-500 bg-blue-50 cursor-wait'
                                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                            }`}
                            onClick={() => handleView(doc.id)}
                            disabled={viewingDocument === String(doc.id)}
                            title={viewingDocument === String(doc.id) ? "Ouverture en cours..." : "Voir le document"}
                          >
                            {viewingDocument === String(doc.id) ? (
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                          <button 
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            onClick={() => handleEdit(doc.id)}
                            title="Éditer"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            onClick={() => handleDownload(doc.id)}
                            title="Télécharger"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            onClick={() => handleShare(doc.id)}
                            title="Partager"
                          >
                            <Share className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                            onClick={() => handleDelete(doc.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
          </div>
        )}
      </div>

      {/* Modal Nouveau Document */}
      <Modal
        isOpen={showNewDocumentModal}
        onClose={() => setShowNewDocumentModal(false)}
        title="Nouveau Document"
        size="lg"
      >
        <NewDocumentForm
          onSubmit={handleNewDocument}
          onCancel={() => setShowNewDocumentModal(false)}
          loading={uploading}
          projects={projects}
          teams={teams}
          documentTypes={documentTypes}
          documentSubtypes={documentSubtypes}
        />
      </Modal>

      {/* Modal Édition Document */}
      <DocumentEditModal
        isOpen={showEditDocumentModal}
        onClose={() => {
          setShowEditDocumentModal(false);
          setEditingDocument(null);
        }}
        onSave={handleUpdateDocument}
        documentName={editingDocument?.title || editingDocument?.name || ''}
        documentId={editingDocument?.id || ''}
        initialData={editingDocument ? {
          name: editingDocument.title || editingDocument.name,
          description: editingDocument.description || '',
          documentType: editingDocument.documentType || '',
          documentSubtype: editingDocument.documentSubtype || '',
          project: editingDocument.project || '',
          team: editingDocument.team || '',
          tags: editingDocument.tags?.join(', ') || '',
          isPublic: editingDocument.isPublic || false,
          syncWith: editingDocument.syncWith || 'local'
        } : undefined}
      />

      {/* Notifications Toast */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

    </DashboardLayout>
  );
};

export default DocumentsPage;
